// Pure helpers for repairing UTF-8-as-Latin-1 mojibake in currency formatting
// strings. Kept in its own file (no server-only imports) so the client bundle
// can import it without dragging Node-only dependencies — see PREM2-30172.

// BAP returns currency formatting strings that have been double-encoded:
// UTF-8 bytes were interpreted as Latin-1 and then re-encoded as UTF-8.
// Examples for the same logical character "¤" (U+00A4):
//   correct:    "¤"   (one JS char, U+00A4)
//   mojibake:   "Â¤"  (two JS chars, U+00C2 U+00A4)
// fixMojibake reverses the double-encoding generically by treating each
// JS character's low byte as a single Latin-1 byte and re-decoding the byte
// stream as UTF-8 with TextDecoder({ fatal: true }). This recovers any
// currency symbol or affix the upstream has mangled (BRL "Â¤" → "¤", EUR
// "Ã©" → "é", JPY "Â¥" → "¥", etc). Inputs that don't look like
// UTF-8-mojibake or whose byte sequence isn't valid UTF-8 pass through
// unchanged.
export function fixMojibake<T extends string | null | undefined>(value: T): T {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  // Cheap detector: any UTF-8 lead byte (0xC2-0xF4) immediately followed by a
  // continuation byte (0x80-0xBF), both showing up as Latin-1 chars in the
  // JS string. If absent, the string isn't mojibake — return as-is.
  if (!/[\u00C2-\u00F4][\u0080-\u00BF]/.test(value)) {
    return value;
  }
  try {
    if (typeof TextDecoder !== 'undefined') {
      const bytes = new Uint8Array(value.length);
      for (let i = 0; i < value.length; i++) bytes[i] = value.charCodeAt(i);
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes) as T;
    }
    // Legacy fallback for environments without a global TextDecoder
    // (e.g. older jsdom-based test runners). escape()/decodeURIComponent
    // is Annex B, supported by every engine, and throws on invalid UTF-8
    // — same fatal semantics as TextDecoder({ fatal: true }).
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return decodeURIComponent(escape(value)) as T;
  } catch {
    return value;
  }
}

// BAP can return any of these formatting fields with the same Latin-1-as-UTF-8
// double-encoding. `groupSep` in particular is ` ` (NBSP) for locales
// like fr-FR, which is exactly the byte sequence most likely to surface as
// "Â<NBSP>" mojibake — so it's covered too.
const SANITIZED_AFFIX_KEYS = [
  'symbol',
  'posPrefix',
  'posSuffix',
  'negPrefix',
  'negSuffix',
  'decimalSep',
  'groupSep',
] as const;

export function sanitizeCurrencySettingsValue<T extends Record<string, any> | null | undefined>(value: T): T {
  if (!value || typeof value !== 'object') return value;
  const source = value as Record<string, any>;
  const sanitized: Record<string, any> = { ...source };
  for (const key of SANITIZED_AFFIX_KEYS) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      sanitized[key] = fixMojibake(source[key]);
    }
  }
  return sanitized as T;
}
